import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Send,
  Loader2,
  MessageSquare,
  Mail,
  Users,
  Search,
  Paperclip,
  X,
  CheckCircle2,
  XCircle,
  ChevronLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // strip data URL prefix to get pure base64
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function BroadcastDialog({ open, onOpenChange, initialClients = [] }) {
  const { api } = useAuth();

  // Step 1: select clients; Step 2: compose message
  const [step, setStep] = useState(1);

  // Client selection
  const [allClients, setAllClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Compose
  const [channel, setChannel] = useState('email'); // 'sms' | 'email' | 'both'
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null); // { name, mime, base64 }
  const fileInputRef = useRef(null);

  // Sending
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null); // broadcast result object

  // Load all clients when dialog opens
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setResults(null);
    setMessage('');
    setSubject('');
    setAttachment(null);
    setSearchQuery('');

    // Pre-select clients passed in
    if (initialClients.length > 0) {
      setSelectedIds(new Set(initialClients.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }

    setLoadingClients(true);
    api
      .get('/clients?limit=1000')
      .then((res) => setAllClients(res.data))
      .catch(() => toast.error('Не удалось загрузить клиентов'))
      .finally(() => setLoadingClients(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredClients = allClients.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q)
    );
  });

  const allSelected =
    filteredClients.length > 0 &&
    filteredClients.every((c) => selectedIds.has(c.id));

  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds);
      filteredClients.forEach((c) => next.delete(c.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      filteredClients.forEach((c) => next.add(c.id));
      setSelectedIds(next);
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Файл слишком большой (макс. 10 МБ)');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setAttachment({ name: file.name, mime: file.type || 'application/octet-stream', base64 });
    } catch {
      toast.error('Не удалось прочитать файл');
    }
    // reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Введите текст сообщения');
      return;
    }
    if (channel === 'email' || channel === 'both') {
      if (!subject.trim()) {
        toast.error('Введите тему письма');
        return;
      }
    }

    setSending(true);
    try {
      const payload = {
        client_ids: Array.from(selectedIds),
        channel,
        subject: subject || undefined,
        message,
        attachment_base64: attachment?.base64 || undefined,
        attachment_name: attachment?.name || undefined,
        attachment_mime: attachment?.mime || undefined,
      };
      const res = await api.post('/clients/broadcast', payload);
      setResults(res.data);
      toast.success(
        `Рассылка завершена: ${res.data.sent_count} отправлено, ${res.data.failed_count} ошибок`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка при отправке рассылки');
    } finally {
      setSending(false);
    }
  };

  const channelOptions = [
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'sms', label: 'SMS', icon: MessageSquare },
    { id: 'both', label: 'Email + SMS', icon: Users },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/50 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-accent" />
            Создать рассылку
          </DialogTitle>
        </DialogHeader>

        {/* Results view */}
        {results ? (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border/50 p-3 text-center">
                  <p className="text-2xl font-bold">{results.total}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Всего</p>
                </div>
                <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-3 text-center">
                  <p className="text-2xl font-bold text-green-500">{results.sent_count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Отправлено</p>
                </div>
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-center">
                  <p className="text-2xl font-bold text-red-400">{results.failed_count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Ошибок</p>
                </div>
              </div>

              {/* Per-client results */}
              <div className="space-y-1.5">
                {results.results.map((r) => (
                  <div
                    key={r.client_id}
                    className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2 text-sm"
                  >
                    <span className="font-medium truncate max-w-[200px]">{r.name}</span>
                    <div className="flex gap-3 text-xs">
                      {r.email !== null && (
                        <span className={cn('flex items-center gap-1', r.email?.success ? 'text-green-500' : 'text-red-400')}>
                          <Mail className="h-3.5 w-3.5" />
                          {r.email?.success ? 'OK' : r.email?.message?.slice(0, 30) || 'Error'}
                        </span>
                      )}
                      {r.sms !== null && (
                        <span className={cn('flex items-center gap-1', r.sms?.success ? 'text-green-500' : 'text-red-400')}>
                          <MessageSquare className="h-3.5 w-3.5" />
                          {r.sms?.success ? 'OK' : r.sms?.message?.slice(0, 30) || 'Error'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t border-border/50 flex-shrink-0">
              <Button onClick={() => onOpenChange(false)}>Закрыть</Button>
            </DialogFooter>
          </div>
        ) : step === 1 ? (
          /* ===== STEP 1: Select clients ===== */
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="px-6 py-3 border-b border-border/50 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">
                  Выбрано: <strong>{selectedIds.size}</strong> из {allClients.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedIds(new Set(allClients.map((c) => c.id)))}
                >
                  Выбрать всех
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Поиск клиентов..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingClients ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-accent" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 pl-6">
                        <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                      </TableHead>
                      <TableHead>Имя</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Телефон</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((c) => (
                      <TableRow
                        key={c.id}
                        className="cursor-pointer"
                        onClick={() => toggleOne(c.id)}
                      >
                        <TableCell className="pl-6">
                          <Checkbox
                            checked={selectedIds.has(c.id)}
                            onCheckedChange={() => toggleOne(c.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.email || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.phone || '—'}</TableCell>
                      </TableRow>
                    ))}
                    {filteredClients.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          Клиенты не найдены
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>

            <DialogFooter className="px-6 py-4 border-t border-border/50 flex-shrink-0">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Отмена
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={selectedIds.size === 0}
              >
                Далее ({selectedIds.size})
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* ===== STEP 2: Compose ===== */
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

              {/* Recipients summary */}
              <div className="flex items-center gap-2 rounded-lg bg-accent/10 border border-accent/20 px-3 py-2">
                <Users className="h-4 w-4 text-accent" />
                <span className="text-sm">
                  Получатели: <strong>{selectedIds.size}</strong> клиент{selectedIds.size !== 1 ? 'ов' : ''}
                </span>
                <button
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground underline"
                  onClick={() => setStep(1)}
                >
                  Изменить
                </button>
              </div>

              {/* Channel selector */}
              <div className="space-y-2">
                <Label>Канал отправки</Label>
                <div className="grid grid-cols-3 gap-2">
                  {channelOptions.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setChannel(id)}
                      className={cn(
                        'flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors',
                        channel === id
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border/60 text-muted-foreground hover:border-accent/50 hover:text-foreground'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject (email only) */}
              {(channel === 'email' || channel === 'both') && (
                <div className="space-y-2">
                  <Label htmlFor="broadcast-subject">Тема письма</Label>
                  <Input
                    id="broadcast-subject"
                    placeholder="Тема..."
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="broadcast-message">
                  Сообщение
                  {channel === 'sms' && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({message.length} символов)
                    </span>
                  )}
                </Label>
                <Textarea
                  id="broadcast-message"
                  placeholder="Текст сообщения..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                />
              </div>

              {/* File attachment (email only) */}
              {(channel === 'email' || channel === 'both') && (
                <div className="space-y-2">
                  <Label>Вложение</Label>
                  {attachment ? (
                    <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 truncate">{attachment.name}</span>
                      <button
                        onClick={() => setAttachment(null)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 px-4 py-3 text-sm text-muted-foreground hover:border-accent/50 hover:text-foreground transition-colors w-full"
                    >
                      <Paperclip className="h-4 w-4" />
                      Прикрепить файл (макс. 10 МБ)
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>

            <DialogFooter className="px-6 py-4 border-t border-border/50 flex-shrink-0 gap-2">
              <Button variant="outline" onClick={() => setStep(1)} disabled={sending}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Назад
              </Button>
              <Button onClick={handleSend} disabled={sending || !message.trim()}>
                {sending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {sending ? 'Отправка...' : `Отправить (${selectedIds.size})`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
